import React, { useRef, useState } from 'react';
import './index.scss'
import AnalysisHeader from './children/AnalysisHeader';
import FilterSection from './children/FilterSection';
import ChartWrapper from './children/ChartWrapper';
import InsufficientSelectionNotice from './children/InsufficientSelectionNotice';
import PdfPreviewModal from '../../../../Components/PdfPreviewModal';
import { buildPdfCover } from '../../../../utils/buildPrintDocument';
import { capturePdfBody } from '../../../../utils/capturePdfBody';
import { fitChartToMaxWidth } from '../../../../utils/chartRegistry';
import TrendComparison from './children/TrendComparison';


const BenchmarkAnalysis = (props) => {
    // Wraps ChartWrapper so we can snapshot its live, filter-driven DOM without
    // touching how it renders. Charts here are inline SVG + DOM tables that clone
    // faithfully; the one exception — the wide, horizontally-scrolling heatmap —
    // is rasterized to a fitted image by capturePdfBody so all columns fit portrait.
    const chartRef = useRef(null);
    const [shownotice, setShowNotice] = useState(false)
    const [preview, setPreview] = useState({ visible: false, html: '', width: undefined, cover: '' });
    const [preparing, setPreparing] = useState(false);

    const handlePreview = async () => {
        const node = chartRef.current;
        if (!node || preparing) return;
        setPreparing(true);
        // html2canvas runs heavy synchronous work; yield two frames first so the
        // button's loading state actually paints before the main thread blocks —
        // otherwise the spinner appears only after rasterization and feels laggy.
        await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
        // Re-render the bar chart to its wrapper's max-width so its baked SVG is
        // capped for the PDF (fits the page with room to spare); restored to its
        // on-screen size afterwards.
        const restoreChart = fitChartToMaxWidth('benchmark-bar-chart');
        await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
        try {
            const { html, width } = await capturePdfBody(node, {
                // Wide grids → fitted images. Only one of the two heatmaps is
                // ever mounted (the trend toggle swaps the whole section list),
                // so naming both here just covers whichever is on screen.
                rasterizeIds: ['heatmap-grid', 'trend-heatmap-grid'],
                // Drop interactive controls the static PDF shouldn't show; the
                // currently-selected view/sort/order stays, only the buttons go.
                stripSelectors: [
                    '.downloadsection', // heatmap's own Excel/Jpg buttons
                    '.divergence__viewtoggle', // Dumbbell / Quadrant toggle
                    '.ratings-spread__sort', // Sort · Favorable % / Spread / A–Z toggle
                ],
            });
            setPreview({
                visible: true,
                html,
                width,
                cover: buildPdfCover({
                    title: 'Summary Report',
                    subtitle: 'Data and Insights from 360 Feedback',
                    reportName: '360 Feedback Analysis',
                    confidential: true,
                    includesText: 'Aggregated insights from the selected dataset, including strengths, growth areas, self vs. others comparisons, and sample comments.',
                }),
            });
        } finally {
            restoreChart(); // restore the on-screen chart to its live width
            setPreparing(false);
        }
    };

    const closePreview = () => setPreview((prev) => ({ ...prev, visible: false }));
    return (
        <>
            <AnalysisHeader onDownload={handlePreview} downloading={preparing} />

            <FilterSection setInsufficientNotice={(value) => { setShowNotice(value) }} />
            <TrendComparison />
            <div ref={chartRef} className='benchmark-analysis__capture'>
                {shownotice ? (
                    <InsufficientSelectionNotice variant='selection' />
                ) : props.isLeaderGateNotMet ? (
                    <InsufficientSelectionNotice variant='self-feedback' />
                ) : (
                    <ChartWrapper />
                )}
            </div>

            <PdfPreviewModal
                visible={preview.visible}
                onCancel={closePreview}
                title='Benchmark Analysis'
                bodyHtml={preview.html}
                contentWidth={preview.width}
                coverHtml={preview.cover}
            />
        </>
    );
};

export default BenchmarkAnalysis;
